import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"

import ReactECharts from "echarts-for-react"

import { getDisplayedFigures, getLegendItemForGrade } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Typography, useTheme, chartsTheme } from "@l2r-front/l2r-ui"
import { getContrastTextColor } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { notationLegend } from "../../constants/getNotationConfig"

import * as Styled from "./NotationTrendingKpi.styled"

const YEAR_IN_MS = 3600 * 24 * 365 * 1000

export function NotationTrendingKpi(props) {
    const {
        values,
    } = props

    const [chartWidth, setChartWidth] = useState(null)
    const [chartHeight, setChartHeight] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const chartRef = useRef(null)

    const theme = useTheme()
    const { t } = useTranslation(I18N_NAMESPACE)

    useEffect(() => {
        const resizeFunction = () => {
            if (chartRef.current) {
                chartRef?.current?.getEchartsInstance().resize()
                setChartWidth(chartRef.current.getEchartsInstance().getWidth())
                setChartHeight(chartRef.current.getEchartsInstance().getHeight())
            }
        }
        window.addEventListener("resize", resizeFunction)
        setTimeout(() => chartRef.current?.getEchartsInstance().resize(), 100)
        return () => {
            window.removeEventListener("resize", resizeFunction)
        }
    }, [chartRef])

    useEffect(() => {
        if (chartRef.current) {
            setChartWidth(chartRef.current.getEchartsInstance().getWidth())
            setChartHeight(chartRef.current.getEchartsInstance().getHeight())
        }
    }, [])

    const getMarkPointOptions = useCallback((value) => {
        if (value === null) {
            return {}
        }

        const legendItemForGrade = getLegendItemForGrade(notationLegend, value[1], theme)

        return {
            coord: value,
            symbolOffset: [0, -30],
            value: getDisplayedFigures(value[1], 2),
            itemStyle: {
                color: theme.palette[legendItemForGrade.color].main,
            },
            label: {
                color: legendItemForGrade.textColor,
                offset: [0, -5],
                fontFamily: "Poppins",
                fontWeight: 600,
            },
        }
    }, [theme])

    const chartOptions = useMemo(() => {
        const currentYear = new Date().getFullYear()
        const breakpointIndex = values.reduce((closestIndex, item, i) => {
            const year = item.date.getFullYear()
            const closestYear = new Date(values[closestIndex].date).getFullYear()

            return Math.abs(year - currentYear) < Math.abs(closestYear - currentYear)
                ? i
                : closestIndex
        }, 0)

        const solidData = values.slice(0, breakpointIndex + 1).map(v => [new Date(Date.UTC(v.date.getUTCFullYear(), 0, 1)), v.grade])
        const dashedData = values.slice(breakpointIndex).map(v => [new Date(Date.UTC(v.date.getUTCFullYear(), 0, 1)), v.grade])

        const solidMarkPointsData = solidData.map(getMarkPointOptions)
        const dashedMarkPointsData = dashedData.map(getMarkPointOptions)

        const smallHeight = chartHeight && chartHeight < 220
        const yAxisRichLabels = Object.fromEntries(
            notationLegend.map(item => [
                item.shortLabel,
                {
                    color: getContrastTextColor(theme.palette[item.color].main),
                    backgroundColor: theme.palette[item.color].main,
                    borderRadius: 16,
                    padding: 4,
                    align: "center",
                    width: smallHeight ? 16 : 20,
                    height: smallHeight ? 12 : 16,
                    fontFamily: "Poppins",
                    fontSize: smallHeight ? 10 : 12,
                    fontWeight: 600,
                },
            ]).reverse(),
        )

        const piecesOptions = notationLegend.map(item => ({
            gt: item.minValue,
            lte: item.maxValue,
            color: theme.palette[item.color].main,
        })).reverse()

        const xCategoryCount = chartWidth ? chartWidth / 80 : 8

        const markPointSymbol = "path://M0.000976564 29.3266L6.9726 36.7841L13.8372 43.9169L20.8053 36.8691L27.777 29.8214L28.0009 0.172091L0.000919878 0.00195325L0.000976564 29.3266Z"

        return {
            grid: {
                top: 50,
                left: 50,
                right: 20,
                bottom: 20,
            },
            xAxis: {
                type: "time",
                min: value => value.min - YEAR_IN_MS / 2,
                max: value => value.max + YEAR_IN_MS / 2,
                minInterval: YEAR_IN_MS,
                maxInterval: YEAR_IN_MS,
                axisLabel: {
                    formatter: function (value) {
                        const date = new Date(value)
                        return date.getFullYear().toString()
                    },
                },
                splitLine: { show: true },
                axisTick: {
                    alignWithLabel: true,
                },
            },
            yAxis: [
                {
                    type: "value",
                    min: notationLegend[0].minValue,
                    max: notationLegend[notationLegend.length - 1].maxValue,
                    interval: notationLegend[notationLegend.length - 1].maxValue / notationLegend.length,
                    axisLabel: { show: false },
                    splitLine: { show: true },
                },
                {
                    type: "category",
                    position: "left",
                    offset: 0,
                    axisTick: { show: false },
                    splitLine: { show: false },
                    data: notationLegend.map(item => item.shortLabel),
                    axisLabel: {
                        formatter: value => `{${value}|${value}}`,
                        rich: yAxisRichLabels,
                        margin: 20,
                    },
                },
            ],
            dataZoom: [
                {
                    type: "inside",
                    filterMode: "none",
                    startValue: Date.now() - YEAR_IN_MS * Math.floor(xCategoryCount / 2),
                    endValue: Date.now() + YEAR_IN_MS * Math.ceil(xCategoryCount / 2),
                    zoomLock: true,
                },
            ],
            visualMap: {
                show: false,
                dimension: 1,
                pieces: piecesOptions,
            },
            series: [
                {
                    type: "line",
                    clip: true,
                    data: solidData.map(d => ({
                        name: "solid",
                        value: d,
                    })),
                    lineStyle: { width: 4, type: "solid" },
                    itemStyle: { borderWidth: 3 },
                    markPoint: {
                        animation: false,
                        symbol: markPointSymbol,
                        symbolSize: [28, 44],
                        data: solidMarkPointsData,
                        emphasis: {
                            disabled: true,
                        },
                    },
                },
                {
                    type: "line",
                    clip: true,
                    data: dashedData.map(d => ({
                        name: "dashed",
                        value: d,
                    })),
                    lineStyle: { width: 4, type: "dashed" },
                    itemStyle: { borderWidth: 3 },
                    markPoint: {
                        animation: false,
                        symbol: markPointSymbol,
                        symbolSize: [28, 44],
                        data: dashedMarkPointsData,
                        emphasis: {
                            disabled: true,
                        },
                    },
                },
            ],
        }
    }, [values, theme, getMarkPointOptions, chartWidth, chartHeight])

    const handleDrag = useCallback(() => {
        setIsDragging(true)
    }, [])

    const handleClick = useCallback((e) => {
        if (isDragging) {
            e.preventDefault()
            e.stopPropagation()
        }
        setIsDragging(false)
    }, [isDragging])

    return (
        <Styled.Wrapper
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleClick}
        >
            <ReactECharts
                ref={chartRef}
                option={chartOptions}
                opts={{ renderer: "svg" }}
                theme={chartsTheme}
                style={{ height: "auto", flex: "1 1 auto" }}
                onEvents={{
                    "datazoom": handleDrag,
                }}
            />
            <Styled.LegendsWrapper >
                <Styled.LegendWrapper>
                    <Styled.StrokeContainer>
                        <Styled.Stroke />
                    </Styled.StrokeContainer>
                    <Typography>
                        {t(I18N_NAMESPACE, "containers.notationTrendingKpi.legend.solid")}
                    </Typography>
                </Styled.LegendWrapper>
                <Styled.LegendWrapper>
                    <Styled.StrokeContainer>
                        <Styled.Stroke />
                        <Styled.Stroke />
                    </Styled.StrokeContainer>
                    <Typography>
                        {t(I18N_NAMESPACE, "containers.notationTrendingKpi.legend.dashed")}
                    </Typography>
                </Styled.LegendWrapper>
            </Styled.LegendsWrapper>
        </Styled.Wrapper >
    )
}